import { useCallback, useEffect, useRef, useState } from "react";
import {
  computeOverflowScrollbarMetrics,
  scrollTopFromThumbDelta,
  scrollTopFromTrackPointer,
} from "../utils/computeOverflowScrollbar";

const EMPTY_METRICS = {
  canScroll: false,
  thumbHeight: 0,
  thumbOffset: 0,
  maxScrollTop: 0,
};

/**
 * Keeps a painted scrollbar in sync with a native overflow container.
 * Overlay scrollbars in Chromium ignore ::-webkit-scrollbar width, so the
 * visible rail has to be a real element.
 * @param {boolean} [enabled]
 * @returns {{
 *   scrollRef: { current: HTMLElement | null },
 *   trackRef: { current: HTMLElement | null },
 *   metrics: {
 *     canScroll: boolean,
 *     thumbHeight: number,
 *     thumbOffset: number,
 *     maxScrollTop: number,
 *   },
 *   handleThumbPointerDown: (event: { button?: number, clientY: number, preventDefault: () => void, stopPropagation: () => void }) => void,
 *   handleTrackPointerDown: (event: { button?: number, clientY: number, target: EventTarget | null, currentTarget: EventTarget | null }) => void,
 * }}
 */
export function useOverflowScrollbar(enabled = true) {
  const scrollRef = useRef(null);
  const trackRef = useRef(null);
  const metricsRef = useRef(EMPTY_METRICS);
  const dragRef = useRef(null);
  const [metrics, setMetrics] = useState(EMPTY_METRICS);

  const updateMetrics = useCallback(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) {
      return;
    }
    const trackEl = trackRef.current;
    const next = computeOverflowScrollbarMetrics({
      scrollHeight: scrollEl.scrollHeight,
      clientHeight: scrollEl.clientHeight,
      scrollTop: scrollEl.scrollTop,
      trackHeight: trackEl ? trackEl.clientHeight : scrollEl.clientHeight,
    });
    metricsRef.current = next;
    setMetrics((prev) => {
      if (
        prev.canScroll === next.canScroll
        && Math.abs(prev.thumbHeight - next.thumbHeight) < 0.5
        && Math.abs(prev.thumbOffset - next.thumbOffset) < 0.5
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    const scrollEl = scrollRef.current;
    if (!scrollEl) {
      return undefined;
    }

    const onScroll = () => {
      updateMetrics();
    };
    scrollEl.addEventListener("scroll", onScroll, { passive: true });

    const resizeObserver = typeof ResizeObserver === "function"
      ? new ResizeObserver(() => {
        updateMetrics();
      })
      : null;
    if (resizeObserver) {
      resizeObserver.observe(scrollEl);
      if (trackRef.current) {
        resizeObserver.observe(trackRef.current);
      }
    }
    window.addEventListener("resize", updateMetrics);

    const mutationObserver = new MutationObserver(() => {
      updateMetrics();
    });
    mutationObserver.observe(scrollEl, { childList: true, subtree: true });

    updateMetrics();

    return () => {
      scrollEl.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", updateMetrics);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      mutationObserver.disconnect();
    };
  }, [enabled, updateMetrics]);

  useEffect(() => {
    const onPointerMove = (event) => {
      const drag = dragRef.current;
      const scrollEl = scrollRef.current;
      if (!drag || !scrollEl) {
        return;
      }
      const current = metricsRef.current;
      const trackEl = trackRef.current;
      const nextScrollTop = scrollTopFromThumbDelta({
        startScrollTop: drag.startScrollTop,
        deltaY: event.clientY - drag.startY,
        trackHeight: trackEl ? trackEl.clientHeight : 0,
        thumbHeight: current.thumbHeight,
        maxScrollTop: current.maxScrollTop,
      });
      if (Number.isFinite(nextScrollTop)) {
        scrollEl.scrollTop = nextScrollTop;
      }
    };

    const onPointerUp = () => {
      dragRef.current = null;
    };

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerUp);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerUp);
    };
  }, []);

  const handleThumbPointerDown = useCallback((event) => {
    if (event.button !== undefined && event.button !== 0) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const scrollEl = scrollRef.current;
    if (!scrollEl) {
      return;
    }
    dragRef.current = {
      startY: event.clientY,
      startScrollTop: scrollEl.scrollTop,
    };
  }, []);

  const handleTrackPointerDown = useCallback((event) => {
    if (event.button !== undefined && event.button !== 0) {
      return;
    }
    if (event.target !== event.currentTarget) {
      return;
    }
    const scrollEl = scrollRef.current;
    const trackEl = trackRef.current;
    if (!scrollEl || !trackEl) {
      return;
    }
    const current = metricsRef.current;
    const trackRect = trackEl.getBoundingClientRect();
    scrollEl.scrollTop = scrollTopFromTrackPointer({
      pointerY: event.clientY,
      trackTop: trackRect.top,
      trackHeight: trackEl.clientHeight,
      thumbHeight: current.thumbHeight,
      maxScrollTop: current.maxScrollTop,
    });
    dragRef.current = {
      startY: event.clientY,
      startScrollTop: scrollEl.scrollTop,
    };
  }, []);

  return {
    scrollRef,
    trackRef,
    metrics,
    handleThumbPointerDown,
    handleTrackPointerDown,
  };
}
